import { useAppDispatch, useAppSelector } from '@/store/store-hooks'
import getUserSettings from '../fetch/get-user-settings'
import patchUserSettings, { UserSettingsPatchPayload } from '../fetch/patch-user-settings'
import { SettingsGetResponse } from '../schemas/settings-schema'
import { chatUxSlice } from '@/chat-common/store/chat-ux.slice'
import {
  selectDefaultJurisdiction,
  selectPromptAssistEnabled,
  selectPromptAssistLastFetched,
  selectPromptAssistFetchIsLoading,
  selectSettingsLoaded,
} from '@/chat-common/store/chat-ux.selectors'

// Cache duration in milliseconds (5 seconds)
const SETTINGS_CACHE_DURATION = 5 * 1000

type UserSettingsFields = {
  prompt_assist_enabled?: boolean | null
  default_jurisdiction?: string | null
}

/**
 * Settings Hook
 * Hook to get company + user settings, and update user settings
 * @returns {getSettings} function to get user settings
 * @returns {updateUserSettings} function to update user settings
 */
export function useSettings() {
  const dispatch = useAppDispatch()
  const promptAssistEnabled = useAppSelector(selectPromptAssistEnabled)
  const defaultJurisdiction = useAppSelector(selectDefaultJurisdiction)
  const lastFetched = useAppSelector(selectPromptAssistLastFetched)
  const isLoading = useAppSelector(selectPromptAssistFetchIsLoading)
  const settingsLoaded = useAppSelector(selectSettingsLoaded)

  const applyUserSettings = (settings: UserSettingsFields) => {
    if (settings.prompt_assist_enabled !== undefined) {
      dispatch(chatUxSlice.actions.setPromptAssistEnabled({ enabled: settings.prompt_assist_enabled ?? true }))
    }

    if (settings.default_jurisdiction !== undefined) {
      dispatch(chatUxSlice.actions.setDefaultJurisdiction({ jurisdiction: settings.default_jurisdiction ?? null }))
    }
  }

  const applySettingsResponse = (response: SettingsGetResponse) => {
    const userSettings = response.data.user
    applyUserSettings({
      prompt_assist_enabled: userSettings.prompt_assist_enabled ?? true,
      default_jurisdiction: userSettings.default_jurisdiction ?? null,
    })
  }

  /**
   * Get Settings
   *
   * Handles:
   * 1. Check cache and return if valid
   * 2. Request settings from server if cache invalid
   * 3. Inserting the settings into the state
   *
   * @param onSuccess
   * @param onError
   */
  const getSettings = async (onSuccess?: () => void, onError?: (e: unknown) => void) => {
    // Check if we are already loading
    if (isLoading) return

    try {
      dispatch(chatUxSlice.actions.setPromptAssistFetchIsLoading({ isLoading: true }))

      // Check if we have valid cached settings
      const now = Date.now()
      if (lastFetched && now - lastFetched < SETTINGS_CACHE_DURATION) {
        if (onSuccess) onSuccess()
        return
      }

      // Ask the server for settings
      const response: SettingsGetResponse = await getUserSettings()
      applySettingsResponse(response)

      dispatch(chatUxSlice.actions.setPromptAssistLastFetched({ lastFetched: now }))

      if (onSuccess) onSuccess()
    } catch (e) {
      if (onError) onError(e)
    } finally {
      dispatch(chatUxSlice.actions.setPromptAssistFetchIsLoading({ isLoading: false }))
    }
  }

  /**
   * Update User Settings
   */
  const updateUserSettings = async (userSettings: UserSettingsPatchPayload, onSuccess?: () => void, onError?: (e: unknown) => void) => {
    try {
      const response = await patchUserSettings(userSettings)
      applyUserSettings({ ...userSettings, ...response.data })

      if (onSuccess) onSuccess()
    } catch (e) {
      if (onError) onError(e)
      throw e
    }
  }

  return { getSettings, updateUserSettings, defaultJurisdiction, promptAssistEnabled, settingsLoaded }
}
